import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import Header from "../components/Header";
import ExecutionCenter from "../components/ExecutionCenter";
import DataMapping from "../components/DataMapping";
import GlobalParameters from "../components/GlobalParameters";
import RangeMatrix from "../components/RangeMatrix";
import BrandHierarchy from "../components/BrandHierarchy";
import { fetchCsvInfo, CATEGORIES } from "../lib/api";
import { Activity, Map, Settings2, GitBranch, Layers } from "lucide-react";

export default function PricingEngine({ onLogout }) {
  // csvInfo and mapping are keyed by category so no category's data ever mixes with another's
  const [csvInfoByCategory, setCsvInfoByCategory] = useState({});
  const [mappingByCategory, setMappingByCategory] = useState({});
  const [tab, setTab] = useState("exec");

  const [vatRate, setVatRate] = useState(20);
  const [minQty, setMinQty] = useState(20);
  const [fallbackTier, setFallbackTier] = useState("E");

  const setMappingFor = (cat, updater) => {
    setMappingByCategory((prev) => {
      const current = prev[cat] || {};
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [cat]: next };
    });
  };

  const reload = async (cat, { silent = false } = {}) => {
    try {
      const info = await fetchCsvInfo(cat, minQty);
      setCsvInfoByCategory((prev) => ({ ...prev, [cat]: info }));
      if (info.loaded && !silent) {
        toast.success(`${info.filename} · ${info.row_count.toLocaleString()} rows`);
      }
    } catch {
      if (!silent) toast.error(`Failed to read CSV folder for ${cat}`);
    }
  };

  useEffect(() => {
    // Quiet check of what's already in the local data folder — no FTP call, no toast.
    // Load Data (FTP fetch) remains the only user-triggered way to bring in fresh data.
    CATEGORIES.forEach((c) => reload(c.value, { silent: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute above/below-threshold counts whenever the Min Quantity Threshold changes
  useEffect(() => {
    CATEGORIES.forEach((c) => {
      if (csvInfoByCategory[c.value]?.loaded) reload(c.value, { silent: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minQty]);

  const primaryCsvInfo = csvInfoByCategory.car || csvInfoByCategory.motorbike || null;

  return (
    <div className="min-h-screen bg-white relative">
      <Header csvInfoByCategory={csvInfoByCategory} onLogout={onLogout} />

      {/* Hero strip */}
      <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] relative overflow-hidden">
        <div className="absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r from-[#1A2340] via-[#F5C800] to-[#1A2340]" />
        <div className="max-w-[1400px] mx-auto px-6 py-8">
          <div className="eyebrow text-[#64748B] mb-3">B2B · Tire Pricing Automation</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#1A2340] leading-[0.9]">
            Margin Engine<br />
            <span className="text-[#F5C800] bg-[#1A2340] px-3">Control Room</span>
          </h1>
          <p className="text-[#64748B] mt-4 max-w-2xl text-sm">
            Load car and motorbike tire data on demand via FTP. Apply tiered markup rules,
            classify brands into a clean hierarchy, and ship priced inventory in seconds.
          </p>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 py-8 relative z-10">
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-transparent border-b border-[#E2E8F0] rounded-none h-auto p-0 w-full justify-start gap-0">
            <TabsTrigger
              value="exec"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-[3px] data-[state=active]:border-[#F5C800] data-[state=active]:text-[#1A2340] text-[#64748B] font-semibold px-5 py-3 gap-2 text-[13px]"
              data-testid="tab-execution"
            >
              <Activity size={14} /> Execution Center
            </TabsTrigger>
            <TabsTrigger
              value="matrix"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-[3px] data-[state=active]:border-[#F5C800] data-[state=active]:text-[#1A2340] text-[#64748B] font-semibold px-5 py-3 gap-2 text-[13px]"
              data-testid="tab-matrix"
            >
              <GitBranch size={14} /> Range Matrix
            </TabsTrigger>
            <TabsTrigger
              value="brands"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-[3px] data-[state=active]:border-[#F5C800] data-[state=active]:text-[#1A2340] text-[#64748B] font-semibold px-5 py-3 gap-2 text-[13px]"
              data-testid="tab-brands"
            >
              <Layers size={14} /> Brand Hierarchy
            </TabsTrigger>
            <TabsTrigger
              value="mapping"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-[3px] data-[state=active]:border-[#F5C800] data-[state=active]:text-[#1A2340] text-[#64748B] font-semibold px-5 py-3 gap-2 text-[13px]"
              data-testid="tab-mapping"
            >
              <Map size={14} /> Data Mapping
            </TabsTrigger>
            <TabsTrigger
              value="params"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-[3px] data-[state=active]:border-[#F5C800] data-[state=active]:text-[#1A2340] text-[#64748B] font-semibold px-5 py-3 gap-2 text-[13px]"
              data-testid="tab-params"
            >
              <Settings2 size={14} /> Global Parameters
            </TabsTrigger>
          </TabsList>

          <TabsContent value="exec" className="mt-6">
            <ExecutionCenter
              csvInfoByCategory={csvInfoByCategory}
              onReload={reload}
              vatRate={vatRate}
              minQty={minQty}
              fallbackTier={fallbackTier}
            />
          </TabsContent>
          <TabsContent value="matrix" className="mt-6">
            <RangeMatrix />
          </TabsContent>
          <TabsContent value="brands" className="mt-6">
            <BrandHierarchy />
          </TabsContent>
          <TabsContent value="mapping" className="mt-6">
            <DataMapping mappingByCategory={mappingByCategory} setMappingFor={setMappingFor} />
          </TabsContent>
          <TabsContent value="params" className="mt-6">
            <GlobalParameters
              vatRate={vatRate}
              setVatRate={setVatRate}
              minQty={minQty}
              setMinQty={setMinQty}
              fallbackTier={fallbackTier}
              setFallbackTier={setFallbackTier}
            />
          </TabsContent>
        </Tabs>
      </main>

      <footer className="border-t border-[#E2E8F0] bg-[#F8F9FA] mt-12">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="eyebrow text-[#64748B]">AttomFit · Pricing Engine v2</div>
          <div className="text-[11px] text-[#64748B] font-mono-data">
            Folder: {primaryCsvInfo?.folder || "—"}
          </div>
        </div>
      </footer>
    </div>
  );
}
